/*
 * Notificações push (Web Push): inscreve este navegador/aparelho para receber os avisos
 * do sistema mesmo com ele fechado (celular e computador). O envio é feito pela API.
 */
import Axios from "@/plugins/Axios";

const SW_URL = "/sw-push.js";

export function suportaPush() {
  return "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
}

export function ehIOS() {
  return (
    /iphone|ipad|ipod/i.test(navigator.userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
  );
}

/** No iPhone, o push só existe com o sistema aberto pela tela de início (como aplicativo). */
export function abertoComoApp() {
  return (
    window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true
  );
}

function chaveParaBytes(base64Url) {
  const base64 = (base64Url + "=".repeat((4 - (base64Url.length % 4)) % 4))
    .replace(/-/g, "+")
    .replace(/_/g, "/");
  return Uint8Array.from(window.atob(base64), (c) => c.charCodeAt(0));
}

function mesmaChave(inscricao, chave) {
  const atual = inscricao.options && inscricao.options.applicationServerKey;
  if (!atual) return true;
  const a = new Uint8Array(atual);
  return a.length === chave.length && a.every((byte, i) => byte === chave[i]);
}

async function registro() {
  const existente = await navigator.serviceWorker.getRegistration(SW_URL);
  return existente || navigator.serviceWorker.register(SW_URL);
}

export async function inscricaoAtual() {
  if (!suportaPush()) return null;
  const reg = await navigator.serviceWorker.getRegistration(SW_URL);
  return reg ? reg.pushManager.getSubscription() : null;
}

/** Push ligado neste aparelho (permissão dada e inscrição ativa). */
export async function pushAtivo() {
  if (!suportaPush() || Notification.permission !== "granted") return false;
  return Boolean(await inscricaoAtual());
}

export async function configuracaoPush() {
  const { data } = await Axios().get("push/configuracao");
  return data;
}

async function enviarInscricao(inscricao) {
  await Axios().post("push/inscricoes", inscricao.toJSON());
}

/** Pede permissão (precisa vir de um toque do usuário) e inscreve este aparelho. */
export async function ativarPush() {
  if (!suportaPush()) {
    throw new Error("Este navegador não permite notificações push.");
  }

  const config = await configuracaoPush();
  if (!config.ativo || !config.chave_publica) {
    throw new Error("As notificações no celular ainda não estão disponíveis neste ambiente.");
  }

  const permissao = await Notification.requestPermission();
  if (permissao !== "granted") {
    throw new Error(
      "A permissão de notificações foi negada. Libere nas configurações do navegador para este site."
    );
  }

  const reg = await registro();
  await navigator.serviceWorker.ready;
  const chave = chaveParaBytes(config.chave_publica);

  let inscricao = await reg.pushManager.getSubscription();
  // a chave do servidor mudou (ex.: chaves novas): a inscrição antiga não serve mais
  if (inscricao && !mesmaChave(inscricao, chave)) {
    await inscricao.unsubscribe();
    inscricao = null;
  }
  if (!inscricao) {
    inscricao = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: chave,
    });
  }

  await enviarInscricao(inscricao);
  return inscricao;
}

export async function desativarPush() {
  const inscricao = await inscricaoAtual();
  if (!inscricao) return;
  try {
    await Axios().delete("push/inscricoes", { data: { endpoint: inscricao.endpoint } });
  } finally {
    await inscricao.unsubscribe();
  }
}

/**
 * Ao entrar no sistema: se este aparelho já tinha push ativo, reenvia a inscrição para
 * ela passar a ser de quem está logado (o aparelho pode ter sido usado por outra pessoa).
 */
export async function sincronizarInscricao() {
  try {
    if (!(await pushAtivo())) return;
    await enviarInscricao(await inscricaoAtual());
  } catch (e) {
    // sem push não é erro: o sininho continua funcionando
  }
}

/** Ao sair: este aparelho para de receber os avisos da conta (sem esperar mais que 2s). */
export async function removerInscricaoNoLogout() {
  try {
    const inscricao = await inscricaoAtual();
    if (!inscricao) return;
    await Promise.race([
      Axios().delete("push/inscricoes", { data: { endpoint: inscricao.endpoint } }),
      new Promise((resolve) => setTimeout(resolve, 2000)),
    ]);
  } catch (e) {
    // segue o logout mesmo se a API não responder
  }
}
